"use client";
import type { DocumentNode } from "graphql";
import { videoBlockDataFragmentDoc } from "@gql/graphql";
import React, { useEffect, useRef, useState } from "react";

export type VideoBlockData = {
  title?: string | null;
  image?: {
    url?: {
      default?: string | null;
      base?: string | null;
    } | null;
  } | null;
  videoAsset?: {
    url?: {
      default?: string | null;
      base?: string | null;
    } | null;
  } | null;
};

interface VideoBlockComponent
  extends React.FC<{ data: VideoBlockData; inEditMode?: boolean }> {
  getDataFragment: () => [string, DocumentNode];
}

const VideoBlock: VideoBlockComponent = ({ data }) => {
  const imageUrl =
    data?.image?.url?.default ?? data?.image?.url?.base ?? undefined;
  const videoUrl =
    data?.videoAsset?.url?.default ?? data?.videoAsset?.url?.base ?? undefined;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    v.addEventListener("play", handlePlay);
    v.addEventListener("pause", handlePause);
    return () => {
      v.removeEventListener("play", handlePlay);
      v.removeEventListener("pause", handlePause);
    };
  }, []);

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;

    try {
      if (v.paused) {
        const p = v.play();
        // Avoid unhandled promise rejections if browser blocks autoplay.
        if (p && typeof (p as Promise<void>).catch === "function") {
          await p.catch(() => {});
        }
      } else {
        v.pause();
      }
    } catch {
      // no-op
    }
  };

  return (
    <div className="flex justify-center">
      <div className="video-block w-full">
        {data?.title && (
          <h3 className="mb-3 text-lg font-semibold text-gray-900">
            {data.title}
          </h3>
        )}

        {videoUrl && (
          <div
            className="relative mt-2 w-full overflow-hidden rounded-lg bg-black"
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
          >
            <video
              ref={videoRef}
              className="h-full w-full rounded-lg object-cover opacity-90"
              src={videoUrl}
              poster={imageUrl}
              preload="metadata"
              playsInline
              controls={false}
            />

            <button
              type="button"
              onClick={togglePlay}
              className={`absolute inset-0 flex items-center justify-center bg-black/10 focus:outline-none transition-opacity ${
                showControls ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
              }`}
              aria-label={isPlaying ? "Pause video" : "Play video"}
            >
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/80 shadow-lg">
                {isPlaying ? (
                  <div className="flex items-center justify-center gap-1">
                    <div className="h-6 w-2 rounded-sm bg-red-600" />
                    <div className="h-6 w-2 rounded-sm bg-red-600" />
                  </div>
                ) : (
                  <div className="ml-1 h-0 w-0 border-y-[10px] border-l-[16px] border-y-transparent border-l-red-600" />
                )}
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

VideoBlock.displayName = "Video Block";

VideoBlock.getDataFragment = (): [string, DocumentNode] => [
  "videoBlockData",
  videoBlockDataFragmentDoc,
];

export default VideoBlock;

